import { labelInLocale } from "./localizedLabels";
import { tMessage } from "./runtime";
import { t } from "./runtime";
import { GOOD_LABEL } from "./labels";
import { withLocaleParticle } from "./particles";

/** Known refusals are localized. Unknown server errors are console-only. */

/** WEAPONS / RESOURCES as the core spells them inside a sentence. */
const good = (label: string, locale: string): string =>
  labelInLocale(GOOD_LABEL, label.trim().toUpperCase(), locale) ?? label;

/**
 * The captured word is a state name, and those are Korean now — 지노, 위본, 하이젠 —
 * so the particle is decidable rather than a coin flip. A name that somehow
 * arrives non-Korean still gets the both-forms spelling from `withLocaleParticle`.
 */
const subject = (name: string, locale: string): string =>
  withLocaleParticle(name, "이가", locale);
const topic = (name: string, locale: string): string =>
  withLocaleParticle(name, "은는", locale);
const andName = (name: string, locale: string): string =>
  withLocaleParticle(name, "와과", locale);

const EXACT: Record<string, string> = {
  // --- market ---
  get "order not found"() {
    return t("errors.order_not_found");
  },
  get "that order is not selling"() {
    return t("errors.this_is_not_a_sell_order");
  },
  get "that order is not buying"() {
    return t("errors.this_is_not_a_buy_order");
  },
  get "not enough cash"() {
    return t("errors.not_enough_cash");
  },
  get "nothing left on that order"() {
    return t("errors.nothing_remains_on_this_order");
  },
  get "quantity must be at least 1"() {
    return t("errors.quantity_must_be_at_least");
  },
  // --- world ---
  get "unknown nation"() {
    return t("errors.unknown_nation");
  },
  get "unknown province"() {
    return t("errors.unknown_province");
  },
  get "that nation no longer exists"() {
    return t("errors.that_nation_no_longer_exists");
  },

  // --- session ---
  get "the session has ended"() {
    return t("errors.the_session_has_ended");
  },
  get "the session has not started"() {
    return t("errors.the_war_has_not_started_yet");
  },
  get "this session cannot run paid agents"() {
    return t("errors.this_session_cannot_use_paid_agents_credentials");
  },
  get "the session is no longer available"() {
    return t("errors.this_session_is_no_longer_available");
  },
  get "session not found"() {
    return t("errors.session_not_found");
  },
  get "not found"() {
    return t("errors.not_found");
  },
  get "too many requests"() {
    return t("errors.too_many_requests_please_try_again_shortly");
  },
  get "internal server error"() {
    return t("errors.a_server_error_occurred");
  },
  get "the server is at session capacity, try again in a moment"() {
    return t("errors.the_server_is_at_session_capacity_please");
  },
  get "no game slot is free right now, try again in a moment"() {
    return t("errors.all_game_slots_are_full_right_now");
  },
  get "this connection already has the maximum number of games running, finish one first"() {
    return t("errors.running_session_limit_for_this_connection");
  },
  get "this connection has too many game pages open, close one and reload"() {
    return t("errors.pending_session_limit_for_this_connection");
  },

  // --- malformed request (a client bug, but it still reaches a toast) ---
  get "invalid request"() {
    return t("errors.invalid_request");
  },
  get "invalid session.join payload"() {
    return t("errors.invalid_request_25");
  },
  get "invalid session.leave payload"() {
    return t("errors.invalid_request_26");
  },
  get "invalid session.player payload"() {
    return t("errors.invalid_request_27");
  },
  get "invalid client.ping payload"() {
    return t("errors.invalid_request_28");
  },

  // --- client network layer ---
  get "the front is unreachable — server offline?"() {
    return t("errors.the_front_is_unreachable_is_the_server");
  },
};

/** Refusals that carry a nation name or a good name inside them. */
const PATTERNS: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [
    /^(.+) no longer holds that stock$/,
    (m) =>
      tMessage("errors.no_longer_holds_that_stock", (_locale) => ({
        p0: subject(m[1], _locale),
      })),
  ],
  // Must precede the plain "cannot pay for that" rule for readability; the
  // shorter pattern is end-anchored, so the two cannot actually collide.
  [
    /^(.+) cannot pay for that, and its credit line is exhausted$/,
    (m) =>
      tMessage("errors.cannot_pay_and_has_no_credit_remaining", (_locale) => ({
        p0: subject(m[1], _locale),
      })),
  ],
  [
    /^(.+) cannot pay for that$/,
    (m) =>
      tMessage("errors.cannot_pay_for_that", (_locale) => ({
        p0: subject(m[1], _locale),
      })),
  ],
  [
    /^(.+) is already at war with (.+)$/,
    (m) =>
      tMessage("errors.is_already_at_war_with", (_locale) => ({
        p0: topic(m[1], _locale),
        p1: andName(m[2], _locale),
      })),
  ],
  [
    /^(.+) does not border (.+)$/,
    (m) =>
      tMessage("errors.does_not_border", (_locale) => ({
        p0: topic(m[1], _locale),
        p1: andName(m[2], _locale),
      })),
  ],
  [
    /^(.+) is not at war with that nation$/,
    (m) =>
      tMessage("errors.is_not_at_war_with_that_nation", (_locale) => ({
        p0: topic(m[1], _locale),
      })),
  ],
  // A gift names ground, and ground changes hands mid-click.
  [
    /^(.+) does not hold that province$/,
    (m) =>
      tMessage("errors.does_not_hold_that_province", (_locale) => ({
        p0: topic(m[1], _locale),
      })),
  ],
  [
    /^(.+) no longer exists$/,
    (m) =>
      tMessage("errors.no_longer_exists", (_locale) => ({
        p0: topic(m[1], _locale),
      })),
  ],
  // The good name is Korean once translated, so the particle can be exact here
  // — unlike the nation-name rules above, where the captured word stays Latin.
  // Not "창고에" — a sale draws on the pickup convoys too, so what the player is
  // short of is stock they own, and saying "창고" would send them to look at the
  // one half of it that was never the whole answer.
  [
    /^the broker holds no (.+)$/,
    (m) =>
      tMessage("errors.no_held_in_the_warehouse_or_in", (_locale) => ({
        p0: withLocaleParticle(good(m[1], _locale), "이가", _locale),
      })),
  ],
  [
    /^warehouse is empty of (.+)$/,
    (m) =>
      tMessage("errors.no_in_the_warehouse", (_locale) => ({
        p0: withLocaleParticle(good(m[1], _locale), "이가", _locale),
      })),
  ],
  [
    /^no (.+) in the warehouse$/,
    (m) =>
      tMessage("errors.no_in_the_warehouse_40", (_locale) => ({
        p0: withLocaleParticle(good(m[1], _locale), "이가", _locale),
      })),
  ],
  [
    /^request failed \((\d+)\)$/,
    (m) => t("errors.request_failed", { p0: m[1] }),
  ],
  [/^unexpected response shape$/, () => t("errors.unexpected_response_format")],
];

export function translateError(message: string): string {
  const trimmed = message.trim();
  const exact = Object.hasOwn(EXACT, trimmed) ? EXACT[trimmed] : undefined;
  if (exact) return exact;

  for (const [pattern, build] of PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) return build(match);
  }
  console.warn("[cwb] Unrecognized refusal", trimmed);
  return t("errors.a_server_error_occurred");
}

/**
 * What the briefing gate tells the player when POST /start is refused.
 *
 * A full house is an expected answer, not a fault: the held session is kept, so
 * the button simply tries again. Anything the dictionary does not know stays
 * the generic connection hint.
 */
export function startRefusalMessage(error: string): string {
  const trimmed = error.trim();
  const known =
    Object.hasOwn(EXACT, trimmed) ||
    PATTERNS.some(([pattern]) => pattern.test(trimmed));
  if (known) return translateError(error);
  console.warn("[cwb] Unrecognized start refusal", trimmed);
  return t("errors.the_war_could_not_be_started_check");
}

/** Exposed so the drift guard can assert coverage. */
export const KNOWN_ERROR_KEYS: readonly string[] = Object.keys(EXACT);
export const ERROR_PATTERNS: readonly RegExp[] = PATTERNS.map(
  ([pattern]) => pattern,
);
